import type {
  IBookingInventoryAddon,
  IBookingInventoryAddonsQuery,
  IBookingOrder,
  IBookingOrderCreated,
  IBookingOrderCreateRequest,
  IBookingOrderPayment,
  IBookingPriceEstimate,
  IBookingPriceEstimateRequest,
  IBookingService,
  IBookingServicesQuery,
  IClientOrder,
  ICompany,
  ICoupon,
  IService,
  IServiceCategory,
} from '@types'
import { getBookingConfig, getBookingDevEnvironment } from '@booking/utils/devEnvironment'
import { logError } from '@booking/utils/logger'
import { getDevApiBaseURL } from '@utils/devEnvironment'
import { createSharedComposable } from '@vueuse/core'

export interface RequestOptions {
  signal?: AbortSignal
}

function createApiFetch(pathPrefix = '') {
  const config = getBookingConfig()
  const backendUrl =
    import.meta.dev && import.meta.client
      ? getDevApiBaseURL(getBookingDevEnvironment()?.settings ?? {})
      : import.meta.server
        ? config.BACKEND_URL || config.public.BACKEND_URL
        : config.public.BACKEND_URL

  return $fetch.create({
    baseURL: `${backendUrl.replace(/\/$/, '')}${pathPrefix}`,
    onResponseError(context) {
      /** Игнорирование ошибок с неправильным именем клиента */
      if ([404].includes(context.response.status)) {
        return
      }
      /** Игнорирование ошибок с неправильным именем клиента */
      if (String(context.request).includes('/clients/get-id/')) {
        return
      }
      logError(context)
    },
    onRequestError(context) {
      logError(context)
    },
  })
}

export const ApiUrls = {
  slots: {
    basic: 'timeslot/basic',
  },
}

function initApi() {
  const cueFetch = createApiFetch('/v1')
  const bookingFetch = createApiFetch()

  return {
    clients: {
      getId: (alias: string) => cueFetch<{ id?: number }>(`clients/get-id/${alias}`),
      get: (id: number) => cueFetch<ICompany>(`clients/get/${id}/subclient`),
    },
    services: {
      get: (id: number) => cueFetch<IService[]>(`services/${id}/small-subclient`, { query: { limit: 2000 } }),
      getService: (companyId: number, serviceId: number) => cueFetch<IService>(`services/${companyId}/${serviceId}`),
    },
    bookingServices: {
      list: (clientAlias: string, query: IBookingServicesQuery, { signal }: { signal?: AbortSignal } = {}) =>
        bookingFetch<IBookingService[]>(`booking/clients/${encodeURIComponent(clientAlias)}/services`, {
          query,
          signal,
        }),
      listInventoryAddons: (
        clientAlias: string,
        serviceId: number,
        query: IBookingInventoryAddonsQuery,
        { signal }: { signal?: AbortSignal } = {},
      ) =>
        bookingFetch<IBookingInventoryAddon[]>(
          `booking/clients/${encodeURIComponent(clientAlias)}/services/${serviceId}/inventory-rent/addon-availability`,
          { query, signal },
        ),
    },
    bookingPrices: {
      estimate: (clientAlias: string, body: IBookingPriceEstimateRequest, { signal }: { signal?: AbortSignal } = {}) =>
        bookingFetch<IBookingPriceEstimate>(`booking/clients/${encodeURIComponent(clientAlias)}/price-estimates`, {
          method: 'POST',
          body,
          signal,
        }),
    },
    bookingOrders: {
      getPayment: (clientAlias: string, uuid: string, paymentId: number) =>
        bookingFetch<IBookingOrderPayment>(
          `booking/clients/${encodeURIComponent(clientAlias)}/orders/${encodeURIComponent(uuid)}/payments/${paymentId}`,
        ),
      get: (clientAlias: string, uuid: string) =>
        bookingFetch<IBookingOrder>(
          `booking/clients/${encodeURIComponent(clientAlias)}/orders/${encodeURIComponent(uuid)}`,
        ),
      create: (clientAlias: string, body: IBookingOrderCreateRequest) =>
        bookingFetch<IBookingOrderCreated>(`booking/clients/${encodeURIComponent(clientAlias)}/orders`, {
          method: 'POST',
          body,
        }),
    },
    serviceCategories: {
      list: (clientId: number, query = {}) => cueFetch<IServiceCategory[]>(`clients/${clientId}/categories`, { query }),
    },
    orders: {
      // Только для редиректа старых числовых ссылок на UUID.
      get: (id: number) => cueFetch<IClientOrder>(`orders/get/${id}`),
      create: async (order: IClientOrder) => {
        const response = await cueFetch<IClientOrder>(`orders/add`, { method: 'POST', body: order })
        if ('_error' in response && response._error) {
          throw Object.assign(
            new Error('message' in response && typeof response.message === 'string' ? response.message : 'Fetch error'),
            { data: response },
          )
        }
        return response
      },
    },
    coupons: {
      check: (data: IClientOrder, { signal }: RequestOptions = {}) =>
        cueFetch<ICoupon>(`coupons/check`, { method: 'POST', body: data, signal }),
    },
    cueFetch,
  }
}

const useApi = createSharedComposable(initApi)
export default useApi
